/**
 * MultiBuy Builder - store.js
 * Central state container. Framework-free pub/sub store that keeps one
 * "bundle context" per curated bundle / create-your-own pack on the page,
 * plus a pointer to whichever context is currently active (open in the
 * bottom sheet or driving the sticky footer).
 *
 * A bundle context shape:
 * {
 *   id: string,                // bundle id (matches DOM data-bundle-id)
 *   type: 'curated' | 'custom',
 *   title: string,
 *   rules: [{products_required, discount_percent}],
 *   items: [{
 *     key,                     // stable slot key (position in curated pack)
 *     productId, productHandle, productTitle, productImage,
 *     variantId, variantTitle, price, compareAtPrice, quantity
 *   }],
 *   replaceIndex: number|null  // set while the bottom sheet is in "replace" mode
 * }
 *
 * Attaches to window.MB.store
 */
(function (window) {
  'use strict';

  var MB = (window.MB = window.MB || {});
  var utils = MB.utils;

  var contexts = {};
  var activeId = null;
  var listeners = {};

  function on(event, handler) {
    listeners[event] = listeners[event] || [];
    listeners[event].push(handler);
    return function off() {
      listeners[event] = listeners[event].filter(function (h) {
        return h !== handler;
      });
    };
  }

  function emit(event, payload) {
    (listeners[event] || []).forEach(function (h) {
      try {
        h(payload);
      } catch (err) {
        console.error('[MultiBuy] listener error for "' + event + '"', err);
      }
    });
  }

  function createContext(config) {
    var ctx = {
      id: config.id,
      type: config.type,
      title: config.title || '',
      description: config.description || '',
      coverImage: config.coverImage || null,
      collectionHandle: config.collectionHandle || null,
      rules: config.rules || [],
      minSlots: config.minSlots || (config.rules && config.rules[0] && config.rules[0].products_required) || 2,
      maxSlots: config.maxSlots || null,
      items: config.items || [],
      replaceIndex: null,
      // Fixed set of product ids that belong in this pack's default
      // "View Collection" view. Unlike items (which shrinks the instant a
      // shopper deselects a product), this only changes when a slot is
      // genuinely swapped via Replace Selection - see collection.js's
      // ensureAutoFilled/handleCardAction. Keeps deselected cards visible
      // (just unchecked) instead of vanishing from the grid.
      packProductIds: config.packProductIds || null
    };
    contexts[ctx.id] = ctx;
    emit('context:created', ctx);
    return ctx;
  }

  function getContext(id) {
    return contexts[id] || null;
  }

  // Every bundle context that currently exists on the page (curated or
  // custom) - used by the "My Cart" popup to show every pack that
  // actually has items, not just whichever one is "active".
  function getAllContexts() {
    return Object.keys(contexts).map(function (id) {
      return contexts[id];
    });
  }

  function getOrCreate(config) {
    return contexts[config.id] || createContext(config);
  }

  function setActive(id) {
    activeId = id;
    emit('active:changed', getContext(id));
  }

  function getActive() {
    return activeId ? getContext(activeId) : null;
  }

  function addItem(bundleId, item) {
    var ctx = getContext(bundleId);
    if (!ctx) return;
    item.key = item.key || utils.generateId('item');
    item.quantity = item.quantity || 1;
    ctx.items.push(item);
    emit('bundle:changed', ctx);
    return ctx;
  }

  function removeItem(bundleId, key) {
    var ctx = getContext(bundleId);
    if (!ctx) return;
    ctx.items = ctx.items.filter(function (i) {
      return i.key !== key;
    });
    emit('bundle:changed', ctx);
    return ctx;
  }

  function replaceItem(bundleId, index, item) {
    var ctx = getContext(bundleId);
    if (!ctx) return;
    var existingKey = ctx.items[index] && ctx.items[index].key;
    item.key = existingKey || utils.generateId('item');
    item.quantity = item.quantity || (ctx.items[index] && ctx.items[index].quantity) || 1;
    ctx.items[index] = item;
    ctx.replaceIndex = null;
    emit('bundle:changed', ctx);
    return ctx;
  }

  function setQuantity(bundleId, key, quantity) {
    var ctx = getContext(bundleId);
    if (!ctx) return;
    var item = ctx.items.filter(function (i) {
      return i.key === key;
    })[0];
    if (!item) return;
    item.quantity = Math.max(1, quantity);
    emit('bundle:changed', ctx);
    return ctx;
  }

  function setReplaceIndex(bundleId, index) {
    var ctx = getContext(bundleId);
    if (!ctx) return;
    ctx.replaceIndex = index;
  }

  function clearBundle(bundleId) {
    var ctx = getContext(bundleId);
    if (!ctx) return;
    ctx.items = [];
    ctx.replaceIndex = null;
    emit('bundle:changed', ctx);
    return ctx;
  }

  function isSelected(bundleId, productId) {
    var ctx = getContext(bundleId);
    if (!ctx) return false;
    return ctx.items.some(function (i) {
      return i.productId === productId;
    });
  }

  MB.store = {
    on: on,
    emit: emit,
    createContext: createContext,
    getContext: getContext,
    getAllContexts: getAllContexts,
    getOrCreate: getOrCreate,
    setActive: setActive,
    getActive: getActive,
    addItem: addItem,
    removeItem: removeItem,
    replaceItem: replaceItem,
    setQuantity: setQuantity,
    setReplaceIndex: setReplaceIndex,
    clearBundle: clearBundle,
    isSelected: isSelected
  };
})(window);
